import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeConfigParamDef, TpETLComponentTypeManifest } from '../interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult, TpETLDryRunObject } from '../../interfaces';
import { addDryRunObject } from '../../utils';
import { getETLDryRunObject } from '../../etlDryRun';
import { getManifestTemplate } from '../factory';
import {
  TpETLComponentTypeTemplate,
  TpETLComponentTypeTemplateParameter,
  TpETLComponentTemplateInputObject,
  TpETLComponentTemplateOutputObject,
} from '../../../../api/models/etl';
import {
  mapTemplateObjectToManifestDataObject,
  createDataObjectFromOutputDefinition,
  updateDataObjectFromInputObjects,
} from './data-type-utils';
import { addETLDryRunError } from '../../etlDryRun';

// TODO: Use input table columns in dry run for validation
function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const componentTemplate = getManifestTemplate(comp.componentType);

  // For each defined output
  Object.entries(comp.outputObjectIds).forEach(([objectKey, objId]) => {
    const outputDefinition = componentTemplate.outputs.find((output) => output.id === objectKey);
    if (!outputDefinition) {
      throw new Error(`Output ${objectKey} not found in component type ${comp.componentType}`);
    }

    // Create a data object with explictitly defined configuration from the template
    const dataObject = createDataObjectFromOutputDefinition(outputDefinition);

    // Transfer additional metadata from the input objects (e.g. input table columns)
    const transferedInputKeys = outputDefinition.metadataTransfer ?? [];
    if (transferedInputKeys.length > 0) {
      const inputObjects: TpETLDryRunObject[] = transferedInputKeys.map((inputKey) =>
        getETLDryRunObject(dryRun, comp.inputObjectIds[inputKey]),
      );
      updateDataObjectFromInputObjects(dataObject, inputObjects, outputDefinition.transferType);
    }

    // Add dry run object to the dry run
    const dryRunObject: TpETLDryRunObject = {
      id: objId,
      createdByCompId: comp.id,
      ...dataObject,
    };
    addDryRunObject(dryRun, dryRunObject);
  });

  // Check required parameters
  componentTemplate.params
    .filter((param) => param.required && !comp.params[param.id])
    .filter((param) => param.paramType.type !== 'boolean') // Skip boolean parameters as they can be false
    .forEach((param) => addETLDryRunError(dryRun, 'This configuration option is required', comp.id, param.id));

  return {
    abort: false,
  };
}

function parameterDefintionToConfigParamType(parameterDefinition: TpETLComponentTypeTemplateParameter) {
  const parameter: TpETLComponentTypeConfigParamDef = {
    id: parameterDefinition.id,
    name: parameterDefinition.name,
    description: parameterDefinition.description,
    paramType: parameterDefinition.paramType,
  };
  return parameter;
}

export function getManifestFromTemplate(manifestTemplate: TpETLComponentTypeTemplate): TpETLComponentTypeManifest {
  const params: TpETLComponentTypeConfigParamDef[] = [];

  return {
    id: manifestTemplate.id,
    name: manifestTemplate.name,
    description: manifestTemplate.description,
    categoryId: manifestTemplate.category,
    icon: <FontAwesomeIcon icon={manifestTemplate.icon || ['fab', 'fa-docker']} />,
    performDryRun,
    inputs: manifestTemplate.inputs.map((input: TpETLComponentTemplateInputObject) => ({
      id: input.id,
      name: input.name,
      description: input.description,
      ...mapTemplateObjectToManifestDataObject(input),
    })),
    outputs: manifestTemplate.outputs.map((output: TpETLComponentTemplateOutputObject) => ({
      id: output.id,
      name: output.name,
      description: output.description,
      ...mapTemplateObjectToManifestDataObject(output),
    })),
    params: manifestTemplate.params.map(parameterDefintionToConfigParamType),
  };
}
